import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';

const initialFormData = {
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: '',
};

const Contact = () => {
    const { t } = useTranslation();
    const { formData, handleChange, setFormData } = useForm(initialFormData);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        // In production, this would send to backend
        console.log('Contact form submitted:', formData);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData(initialFormData);
        }, 3000);
    };

    return (
        <div className="page-transition">
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-4xl font-bold mb-8 text-center">{t('contactPage.title')}</h1>
                    <p className="text-center text-gray-600 mb-12 text-lg">
                        {t('contactPage.subtitle')}
                    </p>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Contact Information */}
                        <div>
                            <h2 className="text-2xl font-semibold mb-6">{t('contactPage.info.title')}</h2>

                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">{t('contactPage.info.emailLabel')}</h3>
                                        <p className="text-gray-600">info@sokba.net</p>
                                        <p className="text-gray-600">support@sokba.net</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">{t('contactPage.info.phoneLabel')}</h3>
                                        <p className="text-gray-600">+32 (0)474 16 28 75</p>
                                        <p className="text-sm text-gray-500">{t('contactPage.info.phoneHours')}</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">{t('contactPage.info.addressLabel')}</h3>
                                        <p className="text-gray-600">Florent Geversstraat, 5</p>
                                        <p className="text-gray-600">B-2650 Edegem</p>
                                        <p className="text-gray-600">Belgium</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold mb-1">{t('contactPage.info.supportHoursLabel')}</h3>
                                        <p className="text-gray-600">{t('contactPage.info.supportHoursWeekdays')}</p>
                                        <p className="text-gray-600">{t('contactPage.info.supportHoursWeekend')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="card">
                            <h2 className="text-2xl font-semibold mb-6">{t('contactPage.form.title')}</h2>

                            {submitted ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                                    <svg className="w-16 h-16 text-green-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="text-xl font-semibold text-green-800 mb-2">
                                        {t('contactPage.form.successTitle')}
                                    </h3>
                                    <p className="text-green-700">
                                        {t('contactPage.form.successText')}
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('contactPage.form.nameLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="input-field"
                                            placeholder={t('contactPage.form.namePlaceholder')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('contactPage.form.emailLabel')}
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="input-field"
                                            placeholder={t('contactPage.form.emailPlaceholder')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('contactPage.form.organizationLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            id="organization"
                                            name="organization"
                                            value={formData.organization}
                                            onChange={handleChange}
                                            className="input-field"
                                            placeholder={t('contactPage.form.organizationPlaceholder')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('contactPage.form.subjectLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            id="subject"
                                            name="subject"
                                            required
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className="input-field"
                                            placeholder={t('contactPage.form.subjectPlaceholder')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('contactPage.form.messageLabel')}
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            required
                                            rows="5"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="input-field"
                                            placeholder={t('contactPage.form.messagePlaceholder')}
                                        />
                                    </div>

                                    <button type="submit" className="btn-primary w-full">
                                        {t('contactPage.form.submitButton')}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
